import React, { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { formatPricePKR } from '../utils/catalog';

const VehicleList = ({
  vehicles,
  loading,
  error,
  categoryTitle,
  onRetry,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
}) => {
  const { setSelectedVehicle, openModal, setCompareStatus, selectedVehicle } = useContext(AppContext);

  const totalVehicles = (vehicles && vehicles.length) || 0;
  const totalPages = Math.max(1, Math.ceil(totalVehicles / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(safePage * pageSize, totalVehicles);
  const paginatedVehicles = (vehicles || []).slice(startIdx, endIdx);

  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, safePage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const openDetail = (vehicle) => {
    setSelectedVehicle(vehicle);
    setCompareStatus(`${vehicle.name} selected`);

    const hasEvSpecs =
      vehicle.powertrain === 'ev' ||
      vehicle.powertrain === 'hybrid' ||
      Boolean(vehicle.battery_kwh) ||
      Boolean(vehicle.range_km);

    let evSpecs = '';
    if (hasEvSpecs) {
      const parts = [];
      if (vehicle.battery_kwh) {
        parts.push(`<p><strong>Battery Capacity:</strong> ${vehicle.battery_kwh} kWh</p>`);
      }
      if (vehicle.range_km) {
        const rangeLabel = vehicle.powertrain === 'ev' ? 'Electric Driving Range' : 'Pure Electric Range';
        parts.push(`<p><strong>${rangeLabel}:</strong> ${vehicle.range_km} km</p>`);
      }
      if (vehicle.ac_charge_kw) {
        parts.push(`<p><strong>AC Charging:</strong> ${vehicle.ac_charge_kw} kW</p>`);
      }
      if (vehicle.dc_charge_kw) {
        parts.push(`<p><strong>DC Fast Charging:</strong> ${vehicle.dc_charge_kw} kW</p>`);
      }
      evSpecs = parts.join('');
    }

    let fuelDisplay = 'N/A';
    if (vehicle.powertrain === 'ev') {
      fuelDisplay = `${vehicle.range_km || 'N/A'} km range`;
    } else if (vehicle.fuel_efficiency) {
      fuelDisplay = `${vehicle.fuel_efficiency} km/L${vehicle.fuel ? ` (${vehicle.fuel})` : ''}`;
    } else if (vehicle.fuel) {
      fuelDisplay = vehicle.fuel;
    }

    let colorsHtml = '';
    if (vehicle.available_colors && vehicle.available_colors.trim()) {
      const colorChips = vehicle.available_colors
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean)
        .map(
          (c) =>
            `<span style="display:inline-block;padding:3px 10px;border-radius:20px;font-size:12px;background:#eef4f8;color:#0b3d54;border:1px solid #c9dbe8;font-weight:500;">${c}</span>`
        )
        .join(' ');

      colorsHtml = `
        <div style="margin:12px 0 8px;">
          <strong style="display:block;margin-bottom:6px;color:#0b3d54;font-size:13px;">
            <i class="fas fa-palette" style="margin-right:6px;color:#0b4f6c;"></i>Available Colors:
          </strong>
          <div style="display:flex;flex-wrap:wrap;gap:6px;">${colorChips}</div>
        </div>`;
    }

    let pricingNotesHtml = '';
    if (vehicle.pricing_notes && vehicle.pricing_notes.trim()) {
      pricingNotesHtml = `
        <div style="background:#fffbeb;border:1.5px solid #fde68a;border-radius:10px;padding:12px 14px;margin:12px 0;color:#92400e;">
          <div style="display:flex;align-items:center;gap:6px;font-weight:700;font-size:13px;margin-bottom:4px;color:#b45309;">
            <i class="fas fa-circle-exclamation"></i> Pricing Notes & Surcharges
          </div>
          <div style="font-size:12.5px;line-height:1.55;color:#78350f;">
            ${vehicle.pricing_notes}
          </div>
        </div>`;
    }

    const body = `
      <div class="simulated-badge">🔬 PROTOTYPE · SIMULATED DATA</div>
      <p><strong>Price:</strong> ${formatPricePKR(vehicle.price)}</p>
      <p><strong>Variant:</strong> ${vehicle.variant || 'Standard'}</p>
      <p><strong>Model Year:</strong> ${vehicle.model_year || '2026'}</p>
      <p><strong>Powertrain:</strong> ${(vehicle.powertrain || '').toUpperCase()}</p>
      <p><strong>Engine:</strong> ${vehicle.engine || 'N/A'}</p>
      <p><strong>Power:</strong> ${vehicle.power || 'N/A'}</p>
      <p><strong>${vehicle.powertrain === 'ev' ? 'Range' : 'Fuel Economy'}:</strong> ${fuelDisplay}</p>
      ${evSpecs}
      <p><strong>Transmission:</strong> ${vehicle.transmission || 'N/A'}</p>
      <p><strong>Dimensions:</strong> ${vehicle.dimensions || 'N/A'}</p>
      <p><strong>Safety:</strong> ${vehicle.safety || 'N/A'}</p>
      <p><strong>Features:</strong> ${vehicle.features || 'N/A'}</p>
      <p><strong>Warranty:</strong> ${vehicle.warranty || 'N/A'}</p>
      <p><strong>Service/Parts:</strong> ${vehicle.service || 'N/A'}</p>
      ${vehicle.ownership ? `<p><strong>Market Appeal / Ownership:</strong> ${vehicle.ownership}</p>` : ''}
      ${colorsHtml}
      ${pricingNotesHtml}
      <p style="font-size:11px;color:#5e7d92;border-top:1px solid #e0eaf3;padding-top:6px;margin-top:6px;">
        Price: ${vehicle.price_source || vehicle.source || 'Official Specs'} · Specs: ${vehicle.spec_source || vehicle.source || 'Official Specs'} · Updated: ${vehicle.source_date || ''} · Model Year: ${vehicle.model_year || ''}
      </p>
      <div style="margin-top:12px;display:flex;flex-wrap:wrap;gap:6px;">
        <button type="button" class="action-btn" data-action="info" data-vehicle-key="${vehicle.key}" data-vehicle-name="${vehicle.name}" data-vehicle-variant="${vehicle.variant || ''}" data-source="product_detail"><i class="fas fa-envelope"></i> Request further info</button>
        <button type="button" class="action-btn secondary" data-action="testdrive" data-vehicle-key="${vehicle.key}" data-vehicle-name="${vehicle.name}" data-vehicle-variant="${vehicle.variant || ''}" data-source="product_detail"><i class="fas fa-key"></i> Book a test drive</button>
        <button type="button" class="action-btn secondary" data-action="finance" data-vehicle-key="${vehicle.key}" data-vehicle-name="${vehicle.name}" data-vehicle-variant="${vehicle.variant || ''}" data-source="product_detail"><i class="fas fa-wallet"></i> Finance this car</button>
      </div>`;
    openModal(
      `${vehicle.name} (${vehicle.category || 'Automobile'})`,
      body,
      `Origin: ${vehicle.origin === 'assembled' ? 'Assembled in Pakistan' : vehicle.origin === 'chinese' ? 'Chinese Brand' : 'Imported'} · Manufacturer: ${(vehicle.mfg || '').toUpperCase()}`,
    );
  };

  if (loading) {
    return (
      <section className="page-section">
        <h2>
          <i className="fas fa-car-side" /> <span>Loading vehicles...</span>
        </h2>
        <div className="model-list">
          <div className="model-list-empty">Loading vehicle catalog…</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="page-section">
        <h2>
          <i className="fas fa-car-side" /> <span>{categoryTitle}</span>
        </h2>
        <div className="model-list">
          <div className="model-list-empty">
            <p>{error}</p>
            <button type="button" className="compare-btn retry-btn" onClick={onRetry}>
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="page-section">
      <h2>
        <i className="fas fa-car-side" /> <span>{categoryTitle}</span>
        <span className="section-count">{totalVehicles}</span>
      </h2>
      <div className="model-list" role="list">
        {totalVehicles === 0 ? (
          <div className="model-list-empty">No vehicles found matching your criteria.</div>
        ) : (
          paginatedVehicles.map((vehicle) => {
            const specChip =
              vehicle.powertrain === 'ev'
                ? `${vehicle.range_km ? `${vehicle.range_km} km` : 'EV'}`
                : vehicle.fuel_efficiency
                ? `${vehicle.fuel_efficiency} km/L`
                : vehicle.fuel || vehicle.engine || 'ICE';

            const colorCount = vehicle.available_colors
              ? vehicle.available_colors
                  .split(',')
                  .map((c) => c.trim())
                  .filter(Boolean).length
              : 0;
            const hasConditions = Boolean(vehicle.pricing_notes && vehicle.pricing_notes.trim());

            const isSelected = selectedVehicle?.key === vehicle.key;
            return (
              <button
                key={vehicle.key}
                type="button"
                className={`model-item${isSelected ? ' selected' : ''}`}
                role="listitem"
                onClick={() => openDetail(vehicle)}
              >
                <div className="model-item-top">
                  <div className="model-item-visual">
                    {vehicle.image_url ? (
                      <img
                        src={vehicle.image_url}
                        alt={vehicle.name}
                        className="model-item-img"
                      />
                    ) : (
                      <span className="model-item-fallback">
                        {vehicle.logo || '🚗'}
                      </span>
                    )}
                  </div>
                  <span className="badge">{(vehicle.mfg || '').toUpperCase()}</span>
                </div>

                <div className="name">{vehicle.name}</div>

                <div className="model-chips">
                  {vehicle.category && <span>{vehicle.category}</span>}
                  {vehicle.variant && <span>{vehicle.variant}</span>}
                  {vehicle.powertrain === 'ev' ? (
                    <span className="chip-ev">EV · {specChip}</span>
                  ) : vehicle.powertrain === 'hybrid' ? (
                    <span className="chip-hybrid">Hybrid · {specChip}</span>
                  ) : (
                    <span>{specChip}</span>
                  )}
                </div>

                {(colorCount > 0 || hasConditions) && (
                  <div className="card-meta-row">
                    {colorCount > 0 && (
                      <span
                        className="card-meta-item card-meta-colors"
                        title={`Available in ${colorCount} colors: ${vehicle.available_colors}`}
                      >
                        <i className="fas fa-palette" /> {colorCount} Colors
                      </span>
                    )}
                    {colorCount > 0 && hasConditions && <span className="card-meta-dot">•</span>}
                    {hasConditions && (
                      <span
                        className="card-meta-item card-meta-conditions"
                        title={vehicle.pricing_notes}
                      >
                        <i className="fas fa-tag" /> Conditions Apply
                      </span>
                    )}
                  </div>
                )}

                <div className="model-item-bottom">
                  <div className="model-price">{formatPricePKR(vehicle.price)}</div>
                  <span className="source-note">
                    {vehicle.source || 'Official Specs'}
                    {vehicle.source_date ? ` · ${vehicle.source_date}` : ''}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>

      {totalVehicles > pageSize && (
        <nav className="catalog-pagination-bar" aria-label="Vehicle catalog pagination">
          <div className="catalog-pagination-info">
            Showing <strong>{startIdx + 1}</strong>–<strong>{endIdx}</strong> of{' '}
            <strong>{totalVehicles}</strong> vehicles · Page <strong>{safePage}</strong> of{' '}
            <strong>{totalPages}</strong>
          </div>

          <div className="catalog-pagination-nav">
            <button
              type="button"
              className="catalog-page-btn catalog-prev-btn"
              onClick={() => onPageChange && onPageChange(safePage - 1)}
              disabled={safePage <= 1}
              aria-label="Previous page"
            >
              <i className="fas fa-chevron-left" /> Previous
            </button>

            <div className="catalog-page-numbers">
              {getPageNumbers().map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`catalog-page-num-btn ${p === safePage ? 'active' : ''}`}
                  onClick={() => onPageChange && onPageChange(p)}
                  aria-label={`Go to page ${p}`}
                  aria-current={p === safePage ? 'page' : undefined}
                >
                  {p}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="catalog-page-btn catalog-next-btn"
              onClick={() => onPageChange && onPageChange(safePage + 1)}
              disabled={safePage >= totalPages}
              aria-label="Next page"
            >
              Next <i className="fas fa-chevron-right" />
            </button>
          </div>
        </nav>
      )}
    </section>
  );
};

export default VehicleList;
